/** Small helpers for brand-tinted surfaces (KPI cards, etc.). */

function normalizeHex(input, fallback = '#1A428A') {
  if (!input || typeof input !== 'string') return fallback;
  let hex = input.trim();
  if (!hex.startsWith('#')) hex = `#${hex}`;
  if (/^#[0-9a-fA-F]{3}$/.test(hex)) {
    hex = `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
  }
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return fallback;
  return hex.toUpperCase();
}

function hexToRgb(hex) {
  const h = normalizeHex(hex);
  return {
    r: parseInt(h.slice(1, 3), 16),
    g: parseInt(h.slice(3, 5), 16),
    b: parseInt(h.slice(5, 7), 16),
  };
}

function toHex({ r, g, b }) {
  const clamp = (n) => Math.max(0, Math.min(255, Math.round(n)));
  return `#${[clamp(r), clamp(g), clamp(b)]
    .map((n) => n.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`;
}

/** Mix brand color toward white (0 = brand, 1 = white). */
export function mixWithWhite(hex, amount = 0.92) {
  const { r, g, b } = hexToRgb(hex);
  const t = Math.max(0, Math.min(1, amount));
  return toHex({
    r: r + (255 - r) * t,
    g: g + (255 - g) * t,
    b: b + (255 - b) * t,
  });
}

/** Darken brand color (0 = unchanged, 1 = black). */
export function darkenHex(hex, amount = 0.18) {
  const { r, g, b } = hexToRgb(hex);
  const t = Math.max(0, Math.min(1, amount));
  return toHex({
    r: r * (1 - t),
    g: g * (1 - t),
    b: b * (1 - t),
  });
}

export function hexToRgba(hex, alpha = 1) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Surfaces for branded KPI cards.
 * Soft = readable dark text on mist; solid = white text on brand.
 */
export function getBrandCardSurface(primaryColor, { featured = false } = {}) {
  const primary = normalizeHex(primaryColor);
  if (featured) {
    const deep = darkenHex(primary, 0.14);
    return {
      mode: 'solid',
      background: `linear-gradient(145deg, ${primary} 0%, ${deep} 100%)`,
      borderColor: hexToRgba(deep, 0.35),
      shadow: `0 8px 24px ${hexToRgba(primary, 0.28)}`,
      titleColor: 'rgba(255,255,255,0.72)',
      contextColor: 'rgba(255,255,255,0.58)',
      valueColor: '#FFFFFF',
      watermarkFilter: 'brightness(0) invert(1)',
      watermarkOpacity: 0.14,
    };
  }

  const mist = mixWithWhite(primary, 0.9);
  const mistEdge = mixWithWhite(primary, 0.96);
  return {
    mode: 'soft',
    background: `linear-gradient(145deg, ${mist} 0%, ${mistEdge} 55%, #FFFFFF 100%)`,
    borderColor: hexToRgba(primary, 0.14),
    shadow: '0 4px 16px rgba(15, 23, 42, 0.08)',
    titleColor: hexToRgba(primary, 0.72),
    contextColor: '#64748B',
    valueColor: '#201C35',
    watermarkFilter: 'none',
    watermarkOpacity: 0.08,
  };
}
